import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { isNull, isNumber, sortBy } from 'lodash-es';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  CreatePurchaseOrderInput,
  Currency,
  GqlService,
  InvoiceStatus,
  listPOQuery,
  UpdatePurchaseOrderInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';
import { NoNullFields, Utils } from '@shared/utils/utils';

import { PurchaseOrderModel } from './purchase-orders.model';
import { PurchaseOrdersStore } from './purchase-orders.store';

@Injectable({ providedIn: 'root' })
export class PurchaseOrdersService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private purchaseOrdersStore = inject(PurchaseOrdersStore);

  PONumbers$ = new BehaviorSubject<{ id: string; po_number: string }[]>([]);

  $PONumbers = toSignal(
    this.PONumbers$.pipe(map((data) => sortBy(data, (el) => el.po_number?.toLowerCase()))),
    { requireSync: true }
  );

  $listPONumbers<R>(fn: (PONumbers: { id: string; po_number: string }[]) => R) {
    return computed(() => fn(this.$PONumbers()));
  }

  async add(input: CreatePurchaseOrderInput) {
    const { data, errors, success } = await firstValueFrom(this.gqlService.createPO$(input));

    if (success && data) {
      this.purchaseOrdersStore.add({
        ...data,
        currency: data.organization?.currency || Currency.USD,
        organization_id: data.organization?.id || null,
        organization_name: data.organization?.name || null,
        paid_amount: null,
        po_report_amount: null,
        po_report_invoiced_amount: null,
        po_report_status: null,
        po_status: data.po_status || null,
        received_amount: null,
      });
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  async deletePurchaseOrder(id: string) {
    this.purchaseOrdersStore.setLoading(true);

    const { data, errors, success } = await firstValueFrom(this.gqlService.removePO$(id));

    if (success && data) {
      this.purchaseOrdersStore.remove(id);
      this.overlayService.success('Successfully removed!');
    } else {
      this.overlayService.error(errors);
    }

    this.purchaseOrdersStore.setLoading(false);
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.purchaseOrdersStore.setLoading(true);

        return this.gqlService.listPO$().pipe(
          tap(({ data, errors, success }) => {
            if (success && data) {
              this.purchaseOrdersStore.set(this.mapListPO(data));
            } else {
              this.overlayService.error(errors);
            }
          })
        );
      })
    );
  }

  getInvoicesThroughOptions(startDate: string, endDate: string) {
    const amountOfMonths = Math.round(Number(Utils.getDurationInMonths(startDate, endDate)));
    const accrualPeriodOptions: Option[] = [];

    for (let month = 0; month < amountOfMonths; month++) {
      const date = dayjs(startDate).add(month, 'month');

      accrualPeriodOptions.push({
        label: date.format('MMMM YYYY'),
        value: `${date.format('YYYY-MM')}`,
      });
    }

    return accrualPeriodOptions;
  }

  getPONumbers() {
    return this.gqlService
      .listPONumbers$()
      .pipe(
        map((x) => {
          return { ...x, data: x.data ? x.data : null };
        })
      )
      .pipe(
        tap(({ data, errors, success }) => {
          if (success && data) {
            this.PONumbers$.next([
              ...data.map((po) => {
                return { id: po.id, po_number: po.po_number };
              }),
            ]);
          } else {
            this.overlayService.error(errors);
          }
        })
      );
  }

  mapListPO = (data: listPOQuery[] | PurchaseOrderModel[] = []) =>
    data.map((po) => {
      const obj: {
        currency: Currency;
        organization_id: null | string;
        organization_name: null | string;
        paid_amount: null | number;
        received_amount: null | number;
      } = {
        currency: po.organization?.currency || Currency.USD,
        organization_id: po.organization?.id || null,
        organization_name: po.organization?.name || null,
        paid_amount: null,
        received_amount: null,
      };

      po.invoice_summaries?.forEach((invoice_summary) => {
        switch (invoice_summary.invoice_status) {
          case InvoiceStatus.STATUS_APPROVED:
            if (isNumber(invoice_summary.total_amount)) {
              if (isNull(obj.paid_amount)) {
                obj.paid_amount = 0;
              }

              obj.paid_amount += invoice_summary.total_amount;
            }

            break;
          case InvoiceStatus.STATUS_IN_QUEUE:
          case InvoiceStatus.STATUS_PENDING_APPROVAL:
          case InvoiceStatus.STATUS_PENDING_REVIEW:
            if (isNumber(invoice_summary.total_amount)) {
              if (isNull(obj.received_amount)) {
                obj.received_amount = 0;
              }

              obj.received_amount += invoice_summary.total_amount;
            }

            break;
          default:
            break;
        }
      });

      return {
        ...po,
        ...obj,
        po_report_amount: isNumber(po.po_report_amount) ? po.po_report_amount : null,
        po_report_invoiced_amount: isNumber(po.po_report_invoiced_amount)
          ? po.po_report_invoiced_amount
          : null,
        po_report_status: po.po_report_status || null,
        po_status: po.po_status || null,
      };
    });

  remove(id: string) {
    this.purchaseOrdersStore.remove(id);
  }

  async update(input: NoNullFields<UpdatePurchaseOrderInput>) {
    const { data, errors, success } = await firstValueFrom(this.gqlService.updatePO$(input));

    if (success && data) {
      this.purchaseOrdersStore.update(input.id, {
        currency: data.organization?.currency || Currency.USD,
        description: input.description,
        organization_id: input.organization_id,
        organization_name: data.organization?.name || null,
        po_number: input.po_number,
        po_status: input.po_status,
      });
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }
}
